import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// ABI for the Chainlink price feed - minimal interface for latestRoundData
const AggregatorV3InterfaceABI = [
  {
    inputs: [],
    name: "decimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "latestRoundData",
    outputs: [
      { internalType: "uint80", name: "roundId", type: "uint80" },
      { internalType: "int256", name: "answer", type: "int256" },
      { internalType: "uint256", name: "startedAt", type: "uint256" },
      { internalType: "uint256", name: "updatedAt", type: "uint256" },
      { internalType: "uint80", name: "answeredInRound", type: "uint80" },
    ],
    stateMutability: "view",
    type: "function",
  },
];

const ChainlinkMapper = ({ 
  contract, 
  setError,
  setSuccess,
  setLoading,
  loading 
}) => {
  // State for the token mappings
  const [tokenMappings, setTokenMappings] = useState({});
  const [newMapping, setNewMapping] = useState({ 
    tokenAddress: '', 
    priceFeedAddress: '',
    tokenName: '',
    network: 'sepolia'
  });
  const [fundTokens, setFundTokens] = useState([]);
  const [tokenPrices, setTokenPrices] = useState({});
  const [priceRefreshing, setPriceRefreshing] = useState(false);

  // Sepolia Chainlink Price Feed addresses - pre-populated for common tokens
  const KNOWN_PRICE_FEEDS = {
    sepolia: {
      "ETH/USD": "0x694AA1769357215DE4FAC081bf1f309aDC325306",
      "BTC/USD": "0x1b44F3514812d835EB1BDB0acB33d3fA3351Ee43",
      "LINK/USD": "0xc59E3633BAAC79493d908e63626716e204A45EdF",
      "DAI/USD": "0x14866185B1962B63C3Ea9E03Bc1da838bab34C19",
      "USDC/USD": "0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E",
      "USDT/USD": "0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E" // Using same as USDC for example
    }
  };

  // Fetch the current mappings from local storage
  useEffect(() => {
    const storedMappings = localStorage.getItem('chainlinkPriceFeedMappings');
    if (storedMappings) {
      try {
        setTokenMappings(JSON.parse(storedMappings));
      } catch (e) {
        console.error("Error parsing stored Chainlink mappings:", e);
        setTokenMappings({});
        // Clear invalid data
        localStorage.removeItem('chainlinkPriceFeedMappings');
      }
    } else {
      // Initialize with empty mappings
      setTokenMappings({});
      localStorage.setItem('chainlinkPriceFeedMappings', JSON.stringify({}));
    }
    
    // Load fund tokens
    if (contract) {
      loadFundTokens();
    }
  }, [contract]);

  // Fetch token prices when fund tokens change
  useEffect(() => {
    if (fundTokens.length > 0) {
      fetchTokenPrices();
    }
  }, [fundTokens]);

  // Load tokens from the fund contract
  const loadFundTokens = async () => {
    try {
      setLoading(true);
      
      // Get token count
      const tokenCount = await contract.getTokenCount();
      const tokens = [];
      
      // Load each token
      for (let i = 0; i < tokenCount; i++) {
        try {
          const tokenInfo = await contract.getTokenInfo(i);
          
          // Try to get token symbol
          let symbol = "Unknown";
          try {
            const tokenContract = new ethers.Contract(
              tokenInfo.tokenAddress,
              ["function symbol() view returns (string)"],
              contract.provider
            );
            symbol = await tokenContract.symbol();
          } catch (err) {
            console.warn(`Could not get symbol for token ${tokenInfo.tokenAddress}`);
          }
          
          tokens.push({
            address: tokenInfo.tokenAddress,
            priceFeed: tokenInfo.priceFeed,
            allocation: tokenInfo.allocation.toString(),
            symbol
          });
        } catch (err) {
          console.error(`Error loading token at index ${i}:`, err);
        }
      }
      
      setFundTokens(tokens);
    } catch (err) {
      setError(`Failed to load fund tokens: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch current prices from Chainlink oracles
  const fetchTokenPrices = async () => {
    if (!contract || !contract.provider) return;
    
    const prices = {};
    setPriceRefreshing(true);
    
    try {
      // Fetch prices for each fund token
      for (const token of fundTokens) {
        try {
          // Create a contract instance for the price feed
          const priceFeedContract = new ethers.Contract(
            token.priceFeed,
            AggregatorV3InterfaceABI,
            contract.provider
          );
          
          // Call latestRoundData to get the current price
          const roundData = await priceFeedContract.latestRoundData();
          
          // Get the number of decimals
          const decimals = await priceFeedContract.decimals();
          
          // Calculate the price in USD
          const price = parseFloat(ethers.utils.formatUnits(roundData.answer, decimals));
          
          // Store the price data
          prices[token.address] = {
            price,
            lastUpdated: new Date(roundData.updatedAt.toNumber() * 1000),
            decimals
          };
        } catch (err) {
          console.error(`Error fetching price for ${token.symbol}:`, err);
          prices[token.address] = { error: 'Failed to fetch price' };
        }
      }
      
      setTokenPrices(prices);
    } catch (err) {
      console.error('Error fetching token prices:', err);
    } finally {
      setPriceRefreshing(false);
    }
  };

  // Add a new mapping
  const addMapping = () => {
    if (!newMapping.tokenAddress || !newMapping.priceFeedAddress) {
      setError('Both token address and price feed address are required');
      return;
    }

    if (!newMapping.tokenAddress.startsWith('0x') || newMapping.tokenAddress.length !== 42) {
      setError('Invalid token address format');
      return;
    }

    if (!newMapping.priceFeedAddress.startsWith('0x') || newMapping.priceFeedAddress.length !== 42) {
      setError('Invalid price feed address format');
      return;
    }

    const normalizedAddress = newMapping.tokenAddress.toLowerCase();
    const updatedMappings = {
      ...tokenMappings,
      [normalizedAddress]: { 
        priceFeedAddress: newMapping.priceFeedAddress,
        tokenName: newMapping.tokenName || 'Unknown',
        network: newMapping.network
      }
    };

    setTokenMappings(updatedMappings);
    localStorage.setItem('chainlinkPriceFeedMappings', JSON.stringify(updatedMappings));
    setSuccess(`Added Chainlink mapping: ${normalizedAddress} → ${newMapping.priceFeedAddress}`);
    
    // Reset form
    setNewMapping({ 
      tokenAddress: '', 
      priceFeedAddress: '',
      tokenName: '',
      network: 'sepolia'
    });
  };

  // Remove a mapping
  const removeMapping = (address) => {
    const updatedMappings = { ...tokenMappings };
    delete updatedMappings[address];
    
    setTokenMappings(updatedMappings);
    localStorage.setItem('chainlinkPriceFeedMappings', JSON.stringify(updatedMappings));
    setSuccess(`Removed Chainlink mapping for ${address}`);
  };

  // Clear all mappings
  const clearAllMappings = () => {
    if (window.confirm('Are you sure you want to remove all Chainlink mappings?')) {
      setTokenMappings({});
      localStorage.setItem('chainlinkPriceFeedMappings', JSON.stringify({}));
      setSuccess('All Chainlink mappings have been cleared');
    }
  };

  // Select a known price feed
  const selectPriceFeed = (pricePair) => {
    setNewMapping({
      ...newMapping,
      priceFeedAddress: KNOWN_PRICE_FEEDS[newMapping.network][pricePair]
    });
  };

  // Copy address to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSuccess('Copied to clipboard');
        setTimeout(() => setSuccess(''), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setError('Failed to copy to clipboard');
      });
  };

  // Update fund's token price feed
  const updateTokenPriceFeed = async (tokenAddress, priceFeedAddress) => {
    try {
      setLoading(true);
      
      // This is a placeholder - in a real implementation, you would call
      // a contract method to update the price feed for a token
      setError("This feature requires a contract method to update price feeds. Please use the admin functions to remove and re-add the token with the new price feed.");
      
      // Example of what the contract call might look like:
      // const tx = await contract.updateTokenPriceFeed(tokenAddress, priceFeedAddress);
      // await tx.wait();
      // setSuccess(`Updated price feed for ${tokenAddress} to ${priceFeedAddress}`);
      
    } catch (err) {
      setError(`Failed to update price feed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format a price with the proper number of decimal places
  const formatPrice = (price) => {
    if (price < 0.01) return price.toFixed(6);
    if (price < 1) return price.toFixed(4);
    if (price < 1000) return price.toFixed(2);
    return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Chainlink Price Feed Mappings</h2>
        <p className="text-gray-600 mb-4">
          Manage the mappings between tokens and Chainlink price feeds. These mappings are used
          to fetch accurate token prices from Chainlink oracles on Sepolia.
        </p>

        {/* Current Fund Tokens */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Current Fund Tokens</h3>
            <button
              onClick={fetchTokenPrices}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center"
              disabled={priceRefreshing || !fundTokens.length}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {priceRefreshing ? "Refreshing..." : "Refresh Prices"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Price (USD)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Price Feed
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fundTokens.map((token, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {token.symbol || "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="truncate max-w-xs" title={token.address}>
                          {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(token.address)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Copy address"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {tokenPrices[token.address] ? (
                        tokenPrices[token.address].error ? (
                          <span className="text-red-500">Error fetching price</span>
                        ) : (
                          <div>
                            <div className="font-medium">${formatPrice(tokenPrices[token.address].price)}</div>
                            <div className="text-xs text-gray-400">
                              Updated: {tokenPrices[token.address].lastUpdated.toLocaleTimeString()}
                            </div>
                          </div>
                        )
                      ) : (
                        <span className="text-gray-400">Loading...</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="truncate max-w-xs" title={token.priceFeed}>
                          {token.priceFeed.substring(0, 6)}...{token.priceFeed.substring(token.priceFeed.length - 4)}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(token.priceFeed)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Copy address"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          // Prefill the form with this token's info
                          setNewMapping({
                            tokenAddress: token.address,
                            priceFeedAddress: '',
                            tokenName: token.symbol || 'Unknown',
                            network: 'sepolia'
                          });
                          // Scroll to the form
                          document.getElementById('new-mapping-form').scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Update Feed
                      </button>
                    </td>
                  </tr>
                ))}
                {fundTokens.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No tokens found in the fund. Add tokens to the fund first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Current Mappings Table */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Saved Chainlink Mappings</h3>
            {Object.keys(tokenMappings).length > 0 && (
              <button
                onClick={clearAllMappings}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price Feed Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Network
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(tokenMappings).map(([address, { priceFeedAddress, tokenName, network }]) => (
                  <tr key={address}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tokenName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="truncate max-w-xs" title={address}>
                          {address.substring(0, 6)}...{address.substring(address.length - 4)}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(address)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Copy address"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="truncate max-w-xs" title={priceFeedAddress}>
                          {priceFeedAddress.substring(0, 6)}...{priceFeedAddress.substring(priceFeedAddress.length - 4)}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(priceFeedAddress)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          title="Copy address"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {network || 'sepolia'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => removeMapping(address)}
                        className="text-red-600 hover:text-red-900 mr-4"
                      >
                        Remove
                      </button>
                      <button
                        onClick={() => {
                          // Find a matching fund token
                          const matchingToken = fundTokens.find(token => 
                            token.address.toLowerCase() === address.toLowerCase()
                          );
                          
                          if (matchingToken) {
                            updateTokenPriceFeed(address, priceFeedAddress);
                          } else {
                            setError("This token is not currently in the fund. Add it to the fund first.");
                          }
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Use in Fund
                      </button>
                    </td>
                  </tr>
                ))}
                {Object.keys(tokenMappings).length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No Chainlink mappings found. Add your first mapping below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add New Mapping Form */}
        <div className="mt-8" id="new-mapping-form">
          <h3 className="text-lg font-medium mb-4">Add New Chainlink Mapping</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Token Address
              </label>
              <input
                type="text"
                value={newMapping.tokenAddress}
                onChange={(e) => setNewMapping({ ...newMapping, tokenAddress: e.target.value })}
                placeholder="0x..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Token Name/Symbol
              </label>
              <input
                type="text"
                value={newMapping.tokenName}
                onChange={(e) => setNewMapping({ ...newMapping, tokenName: e.target.value })}
                placeholder="ETH, BTC, etc."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Network
            </label>
            <select
              value={newMapping.network}
              onChange={(e) => setNewMapping({ ...newMapping, network: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="sepolia">Sepolia</option>
              {/* Add other networks as needed */}
            </select>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chainlink Price Feed Address
            </label>
            <div className="flex">
              <input
                type="text"
                value={newMapping.priceFeedAddress}
                onChange={(e) => setNewMapping({ ...newMapping, priceFeedAddress: e.target.value })}
                placeholder="0x..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Known Price Feed
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.keys(KNOWN_PRICE_FEEDS[newMapping.network]).map((pricePair) => (
                <button
                  key={pricePair}
                  onClick={() => selectPriceFeed(pricePair)}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm hover:bg-gray-200"
                >
                  {pricePair}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <span className="font-bold">Note:</span> For Sepolia testnet, use Chainlink price feeds specific to Sepolia.
              You can find the official list on the <a href="https://docs.chain.link/data-feeds/price-feeds/addresses?network=ethereum-sepolia" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Chainlink documentation</a>.
            </p>
          </div>
          
          <div className="mt-4">
            <button
              type="button"
              onClick={addMapping}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Add Mapping
            </button>
          </div>
        </div>

        {/* Export/Import Buttons */}
        <div className="mt-8 flex space-x-4">
          <button
            onClick={() => {
              const dataStr = JSON.stringify(tokenMappings, null, 2);
              const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
              
              const exportFileDefaultName = 'chainlink-mappings.json';
              
              const linkElement = document.createElement('a');
              linkElement.setAttribute('href', dataUri);
              linkElement.setAttribute('download', exportFileDefaultName);
              linkElement.click();
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Export Mappings
          </button>
          
          <div className="relative">
            <button
              onClick={() => document.getElementById('import-chainlink-mappings').click()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Import Mappings
            </button>
            <input
              id="import-chainlink-mappings"
              type="file"
              accept=".json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    try {
                      const importedMappings = JSON.parse(event.target.result);
                      setTokenMappings(importedMappings);
                      localStorage.setItem('chainlinkPriceFeedMappings', JSON.stringify(importedMappings));
                      setSuccess('Successfully imported Chainlink mappings');
                    } catch (error) {
                      setError('Failed to parse the imported file');
                    }
                  };
                  reader.readAsText(file);
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChainlinkMapper;